/* eslint-disable @typescript-eslint/naming-convention */
/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2019, Bulb Inc. For more information, please email contact@hellobulb.com.
*/

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { merge } from 'lodash-es';

@Injectable({
  providedIn: 'root',
})
export class AppConfigService {
  // Initialize with build-time config, to be updated by loadAppConfig()
  private appConfig:any = JSON.parse(process.env.CONFIG);

  private http: HttpClient;

  constructor(http: HttpClient) {
    this.http = http;
  }

  loadAppConfig() {
    return this.http.get('config')
      .toPromise()
      .then((config) => {
        this.appConfig = merge(this.appConfig, config);
        this.createAJSConfig(this.appConfig);
      });
  }

  public get config(): any {
    return this.appConfig;
  }

  public get unsplash(): unsplashConfig {
    return this.appConfig.unsplash;
  }

  public get uppy(): uppyConfig {
    return this.appConfig.uppy;
  }

  public get sentry(): sentryConfig {
    return this.appConfig.sentry;
  }

  public get adobe(): adobeConfig {
    return this.appConfig.adobe;
  }

  /**
   * Gets the client-safe Canva feature configuration.
   *
   * @returns Canva configuration with the integration disabled when no value is supplied
   */
  public get canva(): canvaConfig {
    return this.appConfig.canva || { enabled: false };
  }

  public get editor(): any {
    return this.appConfig.editor || {};
  }

  public get features(): any {
    return this.appConfig.features || {};
  }

  public get pushnotification(): any {
    return this.appConfig.pushnotification || {};
  }

  public get badgr(): any {
    return this.appConfig.badgr || {};
  }

  public get ngclient(): any {
    return this.appConfig.ngclient || {};
  }

  public get stripe(): TelemetryConfig {
    return this.appConfig.stripe;
  }

  public get telemetry(): TelemetryConfig {
    return this.appConfig.telemetry;
  }

  public get shutdownAlert(): any {
    return this.appConfig.shutdown;
  }

  // eslint-disable-next-line class-methods-use-this
  private createAJSConfig(config: object) {
    window.angular.module('bulbApp.configModule', []).constant('CONFIG', config)
      .run(['$rootScope', '$window', ($rootScope, $window) => {
        const bulbCustomEvent = new CustomEvent('bulbConfig', {
          detail: { config },
        });
        $window.dispatchEvent(bulbCustomEvent);
        // eslint-disable-next-line no-param-reassign
        $rootScope.CONFIG = config;
      }]);
  }
}

export type unsplashConfig = {
  label: string,
  credittype: {
    icon: string,
    none: string,
  },
  client: {
    application: {
      name: string,
    },
  },
};

/**
 * Config for uppy companion instance
 */
export type uppyConfig = {
  maxFileSize: number
  companionURL: string
  debug: boolean
};

export type sentryConfig = {
  client: {
    dsn: string,
    environment: string,
    release: string,
    sampleRate: number,
  },
};

export type adobeConfig = {
  clientId: string,
  appName: string,
};

/**
 * Client-safe runtime configuration for the Canva Uppy integration.
 */
export type canvaConfig = {
  enabled: boolean,
};

/**
 * enabled Enable or disabled Telemetry reporting.
 * debug_mode Configure Telemetry to record events in debug_mode. Used by GA4.
 * GA_MEASUREMENT_ID The GA Property ID used by Google Analytics.
 */
export interface TelemetryConfig {
  enabled: boolean;
  debug_mode: boolean;
  GA_MEASUREMENT_ID: string;
}

export interface StripeConfig {
  enabled: boolean;
  publicKey: string;
}
